import { Body1, makeStyles, tokens } from '@fluentui/react-components';

import type { Agent } from '../../../types/agent.js';
import { useStyles } from '../AgentForm/AgentForm.style.js';

export interface AgentSpecDetailsProps {
  agent: Pick<
    Agent,
    'name' | 'displayName' | 'description' | 'spec' | 'variables'
  >;
}

const usePropertyStyles = makeStyles({
  property: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  label: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  value: {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
});

export function isSensitiveHeader(name: string): boolean {
  return /authorization|cookie|token|secret|api[-_]?key/i.test(name);
}

function Property({ label, value }: { label: string; value?: string }) {
  const styles = usePropertyStyles();
  return (
    <div className={styles.property}>
      <span className={styles.label}>{label}</span>
      <Body1 className={styles.value}>{value ?? 'Not set'}</Body1>
    </div>
  );
}

export function AgentSpecDetails({ agent }: AgentSpecDetailsProps) {
  const styles = useStyles();
  const spec = agent.spec;

  return (
    <div className={styles.root}>
      <div className={styles.scroll}>
        <div className={styles.cards}>
          <section className={styles.card}>
            <div className={styles.cardLabel}>Identity</div>
            <div className={styles.cardBody}>
              <Property label="Name" value={agent.name} />
              <Property label="Display name" value={agent.displayName} />
              <Property label="Description" value={agent.description} />
            </div>
          </section>
          {spec?.agentType === 'AgentLlmSpec' ? (
            <>
              <section className={styles.card}>
                <div className={styles.cardLabel}>Model configuration</div>
                <div className={styles.cardBody}>
                  <Property label="Model name" value={spec.init.modelName} />
                  <Property
                    label="System prompt"
                    value={spec.init.systemPrompt}
                  />
                  {spec.init.temperature !== undefined && (
                    <Property
                      label="Temperature"
                      value={spec.init.temperature}
                    />
                  )}
                  {spec.init.topP !== undefined && (
                    <Property label="Top P" value={spec.init.topP} />
                  )}
                  {spec.init.maxTokens !== undefined && (
                    <Property label="Max tokens" value={spec.init.maxTokens} />
                  )}
                  {spec.init.contextWindow !== undefined && (
                    <Property
                      label="Context window"
                      value={spec.init.contextWindow}
                    />
                  )}
                  {spec.init.includeConversationHistory !== undefined && (
                    <Property
                      label="Include conversation history"
                      value={spec.init.includeConversationHistory}
                    />
                  )}
                </div>
              </section>
              <section className={styles.card}>
                <div className={styles.cardLabel}>Invocation</div>
                <div className={styles.cardBody}>
                  <Property
                    label="User message"
                    value={spec.invoke.userMessage}
                  />
                </div>
              </section>
            </>
          ) : spec?.agentType === 'AgentHttpProxySpec' ? (
            <>
              <section className={styles.card}>
                <div className={styles.cardLabel}>HTTP endpoint</div>
                <div className={styles.cardBody}>
                  <Property
                    label="Base URL"
                    value={spec.init.endpoint.baseUrl}
                  />
                  <Property
                    label="Timeout (seconds)"
                    value={spec.init.endpoint.timeoutSeconds ?? '30'}
                  />
                  <Property
                    label="Retry attempts"
                    value={spec.init.endpoint.retryMaxAttempts ?? '3'}
                  />
                  {spec.init.endpoint.retryDelayMs && (
                    <Property
                      label="Retry delay (ms)"
                      value={spec.init.endpoint.retryDelayMs}
                    />
                  )}
                  {spec.init.processStartInfo && (
                    <Property
                      label="Sidecar command"
                      value={spec.init.processStartInfo.command}
                    />
                  )}
                </div>
              </section>
              <section className={styles.card}>
                <div className={styles.cardLabel}>HTTP invocation</div>
                <div className={styles.cardBody}>
                  <Property
                    label="Method"
                    value={spec.invoke?.method ?? 'POST'}
                  />
                  <Property label="Path" value={spec.invoke?.path ?? '/act'} />
                  {Object.entries(spec.invoke?.headers ?? {}).map(
                    ([name, value]) => (
                      <Property
                        key={name}
                        label={`Header: ${name}`}
                        value={
                          isSensitiveHeader(name)
                            ? 'Configured (hidden)'
                            : value
                        }
                      />
                    ),
                  )}
                  <Property label="Request body" value={spec.invoke?.body} />
                </div>
              </section>
            </>
          ) : spec?.agentType === 'GraderAsAgentSpec' ? (
            <section className={styles.card}>
              <div className={styles.cardLabel}>Grader agent</div>
              <Property label="Grader name" value={spec.init.graderName} />
            </section>
          ) : (
            <Body1 role="alert">
              This agent type is not supported by the editor.
            </Body1>
          )}
          {agent.variables && Object.keys(agent.variables).length > 0 && (
            <section className={styles.card}>
              <div className={styles.cardLabel}>Variables</div>
              <div className={styles.cardBody}>
                {Object.entries(agent.variables).map(([name, declaration]) => (
                  <Property
                    key={name}
                    label={`${name} (${declaration.type})`}
                    value={declaration.description}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
